import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Modal, Table } from 'react-bootstrap';
import { get, onValue, push, ref, remove, update } from 'firebase/database';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';

interface LearningCardSet {
  id: string;
  name: string;
  createdAt?: number;
  updatedAt?: number;
}

const LearningCards = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [sets, setSets] = useState<LearningCardSet[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [editingSet, setEditingSet] = useState<LearningCardSet | null>(null);
  const [limitedStudySet, setLimitedStudySet] = useState<LearningCardSet | null>(null);
  const [limitedStudyCount, setLimitedStudyCount] = useState('20');
  const [studyStartOnBack, setStudyStartOnBack] = useState(false);
  const [ratingStudySet, setRatingStudySet] = useState<LearningCardSet | null>(null);
  const [ratingStudyMinRating, setRatingStudyMinRating] = useState(3);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const setsRef = ref(database, `learning_cards/${currentUser.uid}/sets`);
    const unsubscribe = onValue(setsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, value]: [string, any]) => ({
        id,
        name: value?.name || '',
        createdAt: value?.createdAt,
        updatedAt: value?.updatedAt
      }));
      list.sort((a, b) => (b.updatedAt ?? b.createdAt ?? 0) - (a.updatedAt ?? a.createdAt ?? 0));
      setSets(list);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const canSubmitNewSet = useMemo(() => newSetName.trim().length > 0, [newSetName]);

  const limitedStudyCountValue = useMemo(() => {
    const n = Number(limitedStudyCount);
    if (!Number.isFinite(n)) return null;
    if (n <= 0) return null;
    return Math.floor(n);
  }, [limitedStudyCount]);

  const tryExtractStoragePathFromUrl = (url?: string) => {
    if (!url) return null;
    try {
      const parts = url.split('?')[0].split('/o/');
      if (parts.length < 2) return null;
      return decodeURIComponent(parts[1]);
    } catch {
      return null;
    }
  };

  const deleteStorageObjectIfExists = async (pathOrUrl?: string | null) => {
    if (!pathOrUrl) return;
    const path = pathOrUrl.includes('/o/') ? tryExtractStoragePathFromUrl(pathOrUrl) : pathOrUrl;
    if (!path) return;
    try {
      await deleteObject(storageRef(storage, path));
    } catch (e) {
      console.warn('Failed to delete storage object', e);
    }
  };

  const handleAddSet = async () => {
    if (!currentUser) return;
    const name = newSetName.trim();
    if (!name) return;

    try {
      const setsRef = ref(database, `learning_cards/${currentUser.uid}/sets`);
      const now = Date.now();
      await push(setsRef, {
        name,
        createdAt: now,
        updatedAt: now,
        cards: {}
      });
      setNewSetName('');
      setShowAddModal(false);
      setAlertMessage({ type: 'success', message: 'Set erstellt.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Erstellen des Sets.' });
    }
  };

  const handleDeleteSet = async (setId: string) => {
    if (!currentUser) return;
    if (!window.confirm('Set wirklich löschen? Alle Karten im Set werden ebenfalls gelöscht.')) return;

    try {
      const existingSnap = await get(ref(database, `learning_cards/${currentUser.uid}/sets/${setId}`));
      const existing = existingSnap.val();
      const cards = existing?.cards || {};
      const pathsToDelete: Array<string> = [];

      Object.values(cards).forEach((card: any) => {
        const front = card?.frontImagePath || tryExtractStoragePathFromUrl(card?.frontImageUrl);
        const back = card?.backImagePath || tryExtractStoragePathFromUrl(card?.backImageUrl);
        if (front) pathsToDelete.push(front);
        if (back) pathsToDelete.push(back);
      });

      await Promise.allSettled(pathsToDelete.map((p) => deleteStorageObjectIfExists(p)));
      const setRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}`);
      await remove(setRef);
      setAlertMessage({ type: 'success', message: 'Set gelöscht.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Löschen des Sets.' });
    }
  };

  const handleSaveRename = async () => {
    if (!currentUser || !editingSet) return;
    const name = editingSet.name.trim();
    if (!name) return;

    try {
      const now = Date.now();
      const setRef = ref(database, `learning_cards/${currentUser.uid}/sets/${editingSet.id}`);
      await update(setRef, {
        name,
        updatedAt: now
      });
      setEditingSet(null);
      setAlertMessage({ type: 'success', message: 'Set umbenannt.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Umbenennen des Sets.' });
    }
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Learning Cards</h2>
        <Card>
          <Card.Body>
            <p>Bitte melden Sie sich an, um Learning Cards zu verwenden.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Learning Cards</h2>
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          <Form.Check
            type="switch"
            id="learning-cards-start-on-back"
            label="Rückseite zuerst"
            checked={studyStartOnBack}
            onChange={(e) => setStudyStartOnBack(e.target.checked)}
            className="m-0"
          />
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Neues Set
          </Button>
        </div>
      </div>

      {alertMessage && (
        <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage(null)}>
          {alertMessage.message}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {sets.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: '260px' }}>Lernen</th>
                  <th style={{ width: '260px' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sets.map((setItem) => (
                  <tr key={setItem.id}>
                    <td>
                      <div className="fw-bold">{setItem.name}</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {setItem.updatedAt ? new Date(setItem.updatedAt).toLocaleString('de-CH') : ''}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => {
                            const params = new URLSearchParams();
                            if (studyStartOnBack) params.set('startOnBack', '1');
                            const suffix = params.toString();
                            navigate(`/dashboard/learning-cards/${setItem.id}/study${suffix ? `?${suffix}` : ''}`);
                          }}
                        >
                          Lernen
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => {
                            setLimitedStudySet(setItem);
                            setLimitedStudyCount('20');
                          }}
                        >
                          Lernen (Anzahl)
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => {
                            setRatingStudySet(setItem);
                            setRatingStudyMinRating(3);
                          }}
                        >
                          Lernen (Bewertung)
                        </Button>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button variant="outline-primary" size="sm" onClick={() => navigate(`/dashboard/learning-cards/${setItem.id}`)}>
                          Bearbeiten
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => setEditingSet(setItem)}>
                          Umbenennen
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteSet(setItem.id)}>
                          Löschen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <div className="text-muted mb-3">Noch keine Sets vorhanden.</div>
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Erstes Set erstellen
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neues Set</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                placeholder="z.B. Sprachen, Mathe, Begriffe"
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddSet} disabled={!canSubmitNewSet}>
            Erstellen
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editingSet} onHide={() => setEditingSet(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Set umbenennen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingSet && (
            <Form>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingSet.name}
                  onChange={(e) => setEditingSet({ ...editingSet, name: e.target.value })}
                  autoFocus
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingSet(null)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveRename} disabled={!editingSet?.name.trim()}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!limitedStudySet} onHide={() => setLimitedStudySet(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Lernen (Anzahl)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {limitedStudySet && (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!limitedStudySet || !limitedStudyCountValue) return;
                const params = new URLSearchParams();
                params.set('limit', String(limitedStudyCountValue));
                if (studyStartOnBack) params.set('startOnBack', '1');
                navigate(`/dashboard/learning-cards/${limitedStudySet.id}/study?${params.toString()}`);
                setLimitedStudySet(null);
              }}
            >
              <Form.Group>
                <Form.Label>Anzahl Karten</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={limitedStudyCount}
                  onChange={(e) => setLimitedStudyCount(e.target.value)}
                  autoFocus
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLimitedStudySet(null)}>
            Abbrechen
          </Button>
          <Button
            variant="success"
            disabled={!limitedStudySet || !limitedStudyCountValue}
            onClick={() => {
              if (!limitedStudySet || !limitedStudyCountValue) return;
              const params = new URLSearchParams();
              params.set('limit', String(limitedStudyCountValue));
              if (studyStartOnBack) params.set('startOnBack', '1');
              navigate(`/dashboard/learning-cards/${limitedStudySet.id}/study?${params.toString()}`);
              setLimitedStudySet(null);
            }}
          >
            Lernen starten
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!ratingStudySet} onHide={() => setRatingStudySet(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Lernen (Bewertung)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ratingStudySet && (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!ratingStudySet) return;
                const params = new URLSearchParams();
                params.set('minRating', String(ratingStudyMinRating));
                if (studyStartOnBack) params.set('startOnBack', '1');
                navigate(`/dashboard/learning-cards/${ratingStudySet.id}/study?${params.toString()}`);
                setRatingStudySet(null);
              }}
            >
              <Form.Group>
                <Form.Label>Mindestbewertung (1–5)</Form.Label>
                <Form.Select
                  value={ratingStudyMinRating}
                  onChange={(e) => setRatingStudyMinRating(Number(e.target.value))}
                  autoFocus
                >
                  <option value={1}>1 Stern und höher</option>
                  <option value={2}>2 Sterne und höher</option>
                  <option value={3}>3 Sterne und höher</option>
                  <option value={4}>4 Sterne und höher</option>
                  <option value={5}>5 Sterne</option>
                </Form.Select>
                <div className="text-muted mt-1" style={{ fontSize: '0.9rem' }}>
                  Nur Karten mit dieser Bewertung oder höher werden gelernt.
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRatingStudySet(null)}>
            Abbrechen
          </Button>
          <Button
            variant="success"
            disabled={!ratingStudySet}
            onClick={() => {
              if (!ratingStudySet) return;
              const params = new URLSearchParams();
              params.set('minRating', String(ratingStudyMinRating));
              if (studyStartOnBack) params.set('startOnBack', '1');
              navigate(`/dashboard/learning-cards/${ratingStudySet.id}/study?${params.toString()}`);
              setRatingStudySet(null);
            }}
          >
            Lernen starten
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LearningCards;
